import * as React from 'react'
import { type FeedsCoverage, type PlantLineSummary } from '@axilon-apps/domains-ontology-ui-state'
import { FlowTile, FlowTileCaret } from './FlowTile'
import { SessionGhostTile, type PlantSessionGhostView } from './session-mark-ui'
import { SublineFlowDag } from './SublineFlowDag'
import { UnorderedNotice } from './UnorderedNotice'

/**
 * Flow of child Lines ("Steps") for `LineDetailPage`. The parent page
 * orders them along `Line -[:FEEDS]-> Line` via `orderByFeeds` and passes
 * the coverage verdict (plus the raw edges) down. Rendering picks the
 * richest faithful view:
 *   - `'full'`      — a single linear chain: one caret-separated row.
 *   - `'branching'` / `'partial'` with edges — a connected DAG diagram
 *                     (`SublineFlowDag`), so branches and merges render
 *                     as real arrows instead of a flattened list.
 *   - `'none'` (or no usable edges) — a plain alphabetical list with a
 *                     notice, so a missing flow never reads as a real one.
 *
 * Click navigates into that step's detail page (equipment + HumanLabor).
 */
export interface SublineFlowProps {
  siteId: string
  /** Steps in render order (topological unless `coverage` is `'none'`). */
  sublines: PlantLineSummary[]
  /** How completely the FEEDS edges chain the steps (see `orderByFeeds`). */
  coverage: FeedsCoverage
  /** Directed FEEDS edges between the steps, for the DAG view. */
  feeds?: Array<{ source: string; target: string }>
  /** Deleted steps placed under this line for the current session. */
  ghosts?: PlantSessionGhostView[]
}

const UNORDERED_NOTICE: Record<Exclude<FeedsCoverage, 'full'>, string> = {
  none: 'No FEEDS relationships exist between these steps — listed alphabetically, not in process order.',
  partial:
    'FEEDS relationships cover only some of these steps — steps without a FEEDS edge are shown unconnected.',
  branching:
    'This flow branches or merges — steps are shown in a valid process order, with parallel paths flattened.'
}

export function SublineFlow({
  siteId,
  sublines,
  coverage,
  feeds = [],
  ghosts = []
}: SublineFlowProps): React.ReactElement {
  const ids = React.useMemo(() => new Set(sublines.map((s) => s.id)), [sublines])
  const relevantFeeds = React.useMemo(
    () => feeds.filter((f) => ids.has(f.source) && ids.has(f.target) && f.source !== f.target),
    [feeds, ids]
  )
  const showDag = (coverage === 'branching' || coverage === 'partial') && relevantFeeds.length > 0

  if (showDag) {
    return (
      <div>
        {coverage === 'partial' && <UnorderedNotice>{UNORDERED_NOTICE.partial}</UnorderedNotice>}
        <SublineFlowDag siteId={siteId} sublines={sublines} feeds={relevantFeeds} />
        {ghosts.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {ghosts.map((ghost) => (
              <SessionGhostTile key={ghost.id} name={ghost.name ?? ghost.id} view={ghost.view} />
            ))}
          </div>
        )}
      </div>
    )
  }

  const isOrdered = coverage === 'full'
  return (
    <div>
      {coverage !== 'full' && sublines.length > 0 && (
        <UnorderedNotice>{UNORDERED_NOTICE[coverage]}</UnorderedNotice>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {sublines.map((subline, idx) => (
          <React.Fragment key={subline.id}>
            <FlowTile
              kind="line"
              id={subline.id}
              label="Line"
              node={{ ...subline, siteId }}
              name={subline.name}
              subtitle={`${subline.equipmentCount} ${subline.equipmentCount === 1 ? 'piece' : 'pieces'} of equipment`}
              memberIds={subline.equipmentIds}
            />
            {isOrdered && idx < sublines.length - 1 && <FlowTileCaret />}
          </React.Fragment>
        ))}
        {ghosts.map((ghost) => (
          <SessionGhostTile key={ghost.id} name={ghost.name ?? ghost.id} view={ghost.view} />
        ))}
      </div>
    </div>
  )
}
